/**
 * @quotech/ui/kanban — small utilities used by the components.
 */

import type { Card, Lane, LaneKind } from './types'

// =============================================================================
// Lane-kind metadata: colour + label + AR/AP grouping.
// =============================================================================

const LANE_KIND_LABELS: Record<LaneKind, string> = {
  TRIAGE: 'Triage',
  INBOX_PRIVATE: 'Private',
  NEEDS_REVIEW: 'Needs review',
  WORK: 'Work',
  DRAWINGS: 'Drawings',
  RFI: 'RFI',
  SITE: 'Site',
  PROGRESS: 'Progress',
  CONSENTS: 'Consents',
  BILL_RECEIVED: 'Bill received',
  BILL_RECONCILED: 'Bill reconciled',
  BILL_APPROVED: 'Bill approved',
  BILL_DISPUTED: 'Bill disputed',
  BILL_PAID: 'Bill paid',
  INVOICE_SENT: 'Invoice sent',
  INVOICE_DUE_7: 'Invoice due 7d',
  INVOICE_OVERDUE_30: 'Overdue 1–30d',
  INVOICE_OVERDUE_60: 'Overdue 31–60d',
  INVOICE_OVERDUE_90: 'Overdue 61+d',
  INVOICE_DISPUTED: 'Invoice disputed',
  INVOICE_PAID: 'Invoice paid',
  QUOTE_OPEN: 'Open',
  QUOTE_QUOTING: 'Quoting',
  QUOTE_SUBMITTED: 'Submitted',
  QUOTE_AWARDED: 'Awarded',
  QUOTE_LOST: 'Lost',
  VO_RAISED: 'Raised',
  VO_CLIENT_REVIEW: 'With client',
  VO_QS_REVIEW: 'With QS',
  VO_APPROVED: 'Approved',
  VO_REJECTED: 'Rejected',
  JOB_LEAD: 'Lead',
  JOB_TBC: 'To be confirmed',
  JOB_WIP: 'In production',
  JOB_DONE: 'Complete',
  JOB_BIN: 'Cancelled',
  // FEAT-2026-05-21-KANBAN-WORKFLOW-FOUNDATION — Scrum.
  BACKLOG: 'Backlog',
  READY: 'Ready',
  IN_REVIEW: 'In review',
  QA: 'QA',
  // FEAT-2026-05-21-KANBAN-WORKFLOW-FOUNDATION — CRM lead funnel.
  LEAD_NEW: 'New lead',
  LEAD_CONTACTED: 'Contacted',
  LEAD_QUALIFIED: 'Qualified',
  LEAD_QUOTE_REQUESTED: 'Quote requested',
  LEAD_WON: 'Won',
  LEAD_LOST: 'Lost',
  // FEAT-2026-05-21-KANBAN-WORKFLOW-FOUNDATION — system noise bucket.
  SYSTEM_NOTIFICATIONS: 'System notifications',
  // FEAT-2026-06-01-KANBAN-TEAM-BOARD — team roster availability.
  TEAM_AVAILABLE: 'Available',
  TEAM_ON_JOB: 'On a job',
  TEAM_ON_LEAVE: 'On leave',
  TEAM_INACTIVE: 'Inactive',
  OPS_ACTION: 'Action needed',
  OPS_WAITING: 'Waiting',
  DONE: 'Done',
  ARCHIVED: 'Archived',
  CUSTOM: 'Custom',
  AI_SUGGESTED: 'AI suggested',
  TRELLO_LIST: 'Trello list',
}

const LANE_KIND_COLORS: Record<LaneKind, string> = {
  TRIAGE: '#94A3B8',
  INBOX_PRIVATE: '#7C3AED',
  NEEDS_REVIEW: '#F59E0B',
  WORK: '#0EA5E9',
  DRAWINGS: '#6366F1',
  RFI: '#F59E0B',
  SITE: '#0EA5E9',
  PROGRESS: '#A855F7',
  CONSENTS: '#10B981',
  BILL_RECEIVED: '#0EA5E9',
  BILL_RECONCILED: '#A855F7',
  BILL_APPROVED: '#F59E0B',
  BILL_DISPUTED: '#EF4444',
  BILL_PAID: '#22C55E',
  INVOICE_SENT: '#0EA5E9',
  INVOICE_DUE_7: '#F59E0B',
  INVOICE_OVERDUE_30: '#F97316',
  INVOICE_OVERDUE_60: '#EF4444',
  INVOICE_OVERDUE_90: '#991B1B',
  INVOICE_DISPUTED: '#7C3AED',
  INVOICE_PAID: '#22C55E',
  QUOTE_OPEN: '#0EA5E9',
  QUOTE_QUOTING: '#F59E0B',
  QUOTE_SUBMITTED: '#A855F7',
  QUOTE_AWARDED: '#10B981',
  QUOTE_LOST: '#94A3B8',
  VO_RAISED: '#F97316',
  VO_CLIENT_REVIEW: '#0EA5E9',
  VO_QS_REVIEW: '#A855F7',
  VO_APPROVED: '#10B981',
  VO_REJECTED: '#EF4444',
  JOB_LEAD: '#94A3B8',
  JOB_TBC: '#0EA5E9',
  JOB_WIP: '#F59E0B',
  JOB_DONE: '#10B981',
  JOB_BIN: '#94A3B8',
  BACKLOG: '#94A3B8',
  READY: '#0EA5E9',
  IN_REVIEW: '#A855F7',
  QA: '#EAB308',
  LEAD_NEW: '#94A3B8',
  LEAD_CONTACTED: '#0EA5E9',
  LEAD_QUALIFIED: '#A855F7',
  LEAD_QUOTE_REQUESTED: '#F59E0B',
  LEAD_WON: '#10B981',
  LEAD_LOST: '#94A3B8',
  SYSTEM_NOTIFICATIONS: '#64748B',
  // FEAT-2026-06-01-KANBAN-TEAM-BOARD — team roster availability.
  TEAM_AVAILABLE: '#10B981',
  TEAM_ON_JOB: '#0EA5E9',
  TEAM_ON_LEAVE: '#F59E0B',
  TEAM_INACTIVE: '#94A3B8',
  OPS_ACTION: '#F59E0B',
  OPS_WAITING: '#3B82F6',
  DONE: '#22C55E',
  ARCHIVED: '#475569',
  CUSTOM: '#0EA5E9',
  AI_SUGGESTED: '#A855F7',
  TRELLO_LIST: '#0079BF',
}

/** Every lane kind the FE knows, in declaration order.
 *
 *  Derived from the label map rather than re-listed, so a kind can never
 *  appear in a picker without a label (or vice versa). `workflow-
 *  foundation.pin.test.ts` already pins the map against the BE union, so
 *  this list is exactly the BE's `LaneKind.values`.
 */
export const LANE_KIND_VALUES = Object.keys(LANE_KIND_LABELS) as LaneKind[]

export function getLaneKindLabel(kind: LaneKind): string {
  return LANE_KIND_LABELS[kind] ?? kind
}

export function getLaneKindColor(kind: LaneKind): string {
  return LANE_KIND_COLORS[kind] ?? '#94A3B8'
}

export function getLaneColor(lane: Lane): string {
  return lane.color || getLaneKindColor(lane.kind)
}

// =============================================================================
// AR/AP helpers — the AR/AP board has its own visual grouping.
// =============================================================================

export type ArApSide = 'AR' | 'AP'

const AR_LANE_KINDS: LaneKind[] = [
  'INVOICE_SENT', 'INVOICE_DUE_7', 'INVOICE_OVERDUE_30',
  'INVOICE_OVERDUE_60', 'INVOICE_OVERDUE_90',
  'INVOICE_DISPUTED', 'INVOICE_PAID',
]

const AP_LANE_KINDS: LaneKind[] = [
  'BILL_RECEIVED', 'BILL_RECONCILED', 'BILL_APPROVED',
  'BILL_DISPUTED', 'BILL_PAID',
]

export function getArApSide(lane: Lane): ArApSide | null {
  if (AR_LANE_KINDS.includes(lane.kind)) return 'AR'
  if (AP_LANE_KINDS.includes(lane.kind)) return 'AP'
  return null
}

// =============================================================================
// Card sorting + sensitivity flag helpers.
// =============================================================================

/** Default in-lane card comparator. Key order:
 *    pinned ↑ → active-before-DONE → priority ↓ → position ↑ →
 *    due_date ↑ → created_at ↓.
 *
 *  PINNED-FIRST (BUG-2026-06-29): `Card.pinned`'s documented contract is
 *  that a pinned card "sorts above non-pinned cards in its lane REGARDLESS
 *  of position / priority / due_date" (see types.ts). It stays the top key.
 *
 *  DONE-SINK (FEAT-2026-07-20-KANBAN-PRIORITY-LANE-ORDER): a completed card
 *  must not float to the top of a lane just because it is Urgent. Active
 *  cards sort above `status === 'DONE'` ones so live work leads regardless of
 *  priority. (Checklist-mode lanes additionally collapse DONE into a footer;
 *  this key governs the non-checklist lanes where DONE stays inline.)
 *
 *  PRIORITY-FIRST (FEAT-2026-07-20-KANBAN-PRIORITY-LANE-ORDER): a card set to
 *  a higher priority automatically rises in its lane (Urgent → High → Normal →
 *  Low) on EVERY board. This deliberately SUPERSEDES the previous
 *  position-above-priority ordering (FEAT-2026-07-02-KANBAN-DRAG-REORDER): the
 *  operator's manual drag `position` now orders cards only WITHIN a priority
 *  band, not across bands. Because every card defaults to priority 0, a lane
 *  where nobody set a priority is byte-identical to the old position order —
 *  only cards a user explicitly prioritised move. A cross-band drag ADOPTS the
 *  target band's priority (see cardReorder.computeDropAdoptPriority), so the
 *  drop still "sticks" rather than snapping back under the comparator.
 *
 *  POSITION-WITHIN-BAND (FEAT-2026-07-02-KANBAN-DRAG-REORDER): `Card.position`
 *  is the BE's float lane-ordering key (auto-set to `max + 10` on create,
 *  rewritten by a drag-reorder move). Within one priority band a dragged card
 *  stays where it was dropped; legacy rows with no stored position sort AFTER
 *  positioned ones and keep the due → created ordering among themselves.
 *
 *  An explicit user-chosen lane sort still overrides all of this via the
 *  lane's `sortComparator`; only the default/unset order is affected. */
export function compareCards(a: Card, b: Card): number {
  const ap = a.pinned ? 1 : 0
  const bp = b.pinned ? 1 : 0
  if (ap !== bp) return bp - ap
  // Active (any non-DONE status) sorts above DONE, so a completed card never
  // floats to the top of its lane on the strength of its priority.
  const adone = a.status === 'DONE' ? 1 : 0
  const bdone = b.status === 'DONE' ? 1 : 0
  if (adone !== bdone) return adone - bdone
  // Priority DESC — the primary automatic ordering. Urgent(3) → Low(0).
  const apri = a.priority ?? 0
  const bpri = b.priority ?? 0
  if (apri !== bpri) return bpri - apri
  // Manual drag order WITHIN a priority band.
  const apos =
    typeof a.position === 'number' && Number.isFinite(a.position) ? a.position : null
  const bpos =
    typeof b.position === 'number' && Number.isFinite(b.position) ? b.position : null
  if (apos !== null && bpos !== null) {
    if (apos !== bpos) return apos - bpos
  } else if (apos !== null) {
    return -1
  } else if (bpos !== null) {
    return 1
  }
  if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date)
  if (a.due_date) return -1
  if (b.due_date) return 1
  return b.created_at.localeCompare(a.created_at)
}

/** Date-descending comparator (newest first) keyed on the EMAIL's received
 *  date — the date actually printed on an email-backed card — falling back
 *  to `created_at` for non-email cards. Pinned cards still float to the top.
 *
 *  Used by the Time (Gantt bucket) view so cards inside each time column
 *  read newest-first, which is what operators expect when a column already
 *  groups by date range (operator 2026-06-29: "they are not in decreasing
 *  order of date in each column"). The default `compareCards` ordering
 *  (priority → due ↑) is kept for the workflow Board view. */
export function compareByDateDesc(a: Card, b: Card): number {
  const ap = a.pinned ? 1 : 0
  const bp = b.pinned ? 1 : 0
  if (ap !== bp) return bp - ap
  const ad = a.primary_email_received_at || a.created_at
  const bd = b.primary_email_received_at || b.created_at
  return bd.localeCompare(ad)
}

export function sortCards(cards: Card[]): Card[] {
  return [...cards].sort(compareCards)
}

/** Stack 12 MR-C — flatten a list of cards into the depth-first parent
 *  → children traversal order the lane renderer expects. Each entry
 *  carries the indent step so the renderer doesn't have to walk the
 *  tree itself.
 *
 *  Inputs are the lane's visible cards (already sort-filtered + paged).
 *  Output preserves `sortCards` ordering WITHIN each sibling group and
 *  emits a card immediately followed by its descendants (Notion-style
 *  depth-first).
 *
 *  "Orphan" cards — those whose `parent_card` references a card not
 *  in the input set (e.g. parent is archived, or on another lane after
 *  a cross-lane move, or simply not in this page of cards) — are
 *  treated as roots and rendered at depth 0 so they remain visible.
 *  This prevents a stale parent FK from making a card invisible.
 *
 *  Pure I→O so it's safely memoisable in the lane component. */
export function flattenCardTree(
  cards: Card[],
  comparator: (a: Card, b: Card) => number = compareCards,
): Array<{ card: Card; indent: number }> {
  if (cards.length === 0) return []
  const byId = new Map<string, Card>()
  for (const c of cards) byId.set(c.id, c)
  const childrenOf = new Map<string | null, Card[]>()
  for (const c of cards) {
    const key =
      c.parent_card && byId.has(c.parent_card) ? c.parent_card : null
    const bucket = childrenOf.get(key)
    if (bucket) bucket.push(c)
    else childrenOf.set(key, [c])
  }
  for (const [key, bucket] of childrenOf.entries()) {
    childrenOf.set(key, [...bucket].sort(comparator))
  }
  const out: Array<{ card: Card; indent: number }> = []
  const visit = (parentId: string | null, depth: number) => {
    const kids = childrenOf.get(parentId)
    if (!kids) return
    for (const c of kids) {
      out.push({ card: c, indent: depth })
      // Cap recursion at the BE-enforced depth-5 ceiling. Anything
      // deeper means corrupted data — render flat rather than spin.
      if (depth < 5) visit(c.id, depth + 1)
    }
  }
  visit(null, 0)
  return out
}

export function ageInDays(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null
  const ms = Date.now() - new Date(isoDate).getTime()
  return Math.floor(ms / (1000 * 60 * 60 * 24))
}

export function formatRelativeAge(isoDate: string | null | undefined): string {
  const days = ageInDays(isoDate)
  if (days === null) return ''
  if (days === 0) return 'today'
  if (days === 1) return '1d ago'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

// =============================================================================
// Contrast helpers — used by tag chips, lane stripes, anything that takes
// an arbitrary user-picked colour and needs a readable foreground.
//
// We compute relative luminance per WCAG 2.1 §1.4.3. Threshold of 0.55 is
// chosen empirically to match the Trello / Linear feel: pastels go dark,
// jewel tones go light, exactly at the eyeball's "this is bright" boundary.
// =============================================================================

/** Parse a `#rrggbb` / `#rgb` / `rgb(...)` colour into RGB 0-255 triplet.
 *  Returns null on un-parseable input — caller falls back to safe default. */
function parseColor(input: string | undefined | null): [number, number, number] | null {
  if (!input) return null
  const s = input.trim()
  if (s.startsWith('#')) {
    const hex = s.slice(1)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16)
      const g = parseInt(hex[1] + hex[1], 16)
      const b = parseInt(hex[2] + hex[2], 16)
      return Number.isFinite(r + g + b) ? [r, g, b] : null
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return Number.isFinite(r + g + b) ? [r, g, b] : null
    }
    return null
  }
  const m = s.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (m) return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)]
  return null
}

/** Relative luminance per WCAG. 0 = pure black, 1 = pure white. */
export function relativeLuminance(color: string | undefined | null): number {
  const rgb = parseColor(color)
  if (!rgb) return 0.5
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Pick a readable foreground (black/white) for an arbitrary background.
 *  Returns the hex colour string ready to drop into `style.color`. */
export function readableForeground(bgColor: string | undefined | null): string {
  return relativeLuminance(bgColor) > 0.55 ? '#1f2937' : '#f8fafc'
}
